/**
 * Damerau-Levenshtein edit distance with optional early exit.
 *
 * Returns the minimum number of single-character insertions, deletions,
 * substitutions, OR transpositions of adjacent characters needed to
 * transform `a` into `b`. Transposition is what differentiates this from
 * plain Levenshtein and is what makes "pilto" -> "pilot" cost 1 instead
 * of 2.
 *
 * If `maxDistance` is provided, the function returns `maxDistance + 1`
 * as soon as every entry in the current row exceeds the budget. This is
 * the cheap-reject path we use when scanning the token index for fuzzy
 * matches: most token pairs are not candidates and we want to abandon
 * them in O(min(a,b) * maxDistance), not O(a*b).
 */
export function damerauLevenshtein(a, b, maxDistance) {
  if (a === b) return 0;
  const la = a.length;
  const lb = b.length;
  if (la === 0) return lb;
  if (lb === 0) return la;

  // Fast length-difference reject: if |la-lb| already exceeds the budget,
  // no edit sequence can bring them within range.
  if (typeof maxDistance === "number" && Math.abs(la - lb) > maxDistance) {
    return maxDistance + 1;
  }

  // Two rolling rows is enough for Levenshtein; Damerau needs the row
  // from two steps back to detect transpositions, so we keep three rows.
  let prev2 = null;
  let prev = new Array(lb + 1);
  let curr = new Array(lb + 1);
  for (let j = 0; j <= lb; j++) prev[j] = j;

  for (let i = 1; i <= la; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    const ai = a.charCodeAt(i - 1);
    for (let j = 1; j <= lb; j++) {
      const cost = ai === b.charCodeAt(j - 1) ? 0 : 1;
      let v = Math.min(
        curr[j - 1] + 1,          // insertion
        prev[j] + 1,              // deletion
        prev[j - 1] + cost        // substitution
      );
      // Transposition: a[i-1]==b[j-2] && a[i-2]==b[j-1]
      if (
        i > 1 && j > 1 && prev2 &&
        ai === b.charCodeAt(j - 2) &&
        a.charCodeAt(i - 2) === b.charCodeAt(j - 1)
      ) {
        v = Math.min(v, prev2[j - 2] + 1);
      }
      curr[j] = v;
      if (v < rowMin) rowMin = v;
    }
    if (typeof maxDistance === "number" && rowMin > maxDistance) {
      return maxDistance + 1;
    }
    // Advance rows: prev2 <- prev, prev <- curr, curr becomes scratch.
    const scratch = prev2 || new Array(lb + 1);
    prev2 = prev;
    prev = curr;
    curr = scratch;
  }
  return prev[lb];
}


/**
 * Edit-distance budget for a query token of length n.
 *   n <= 2   -> 0 edits (too short to typo-correct meaningfully)
 *   3 <= n <= 7 -> 1 edit
 *   n >= 8   -> 2 edits
 *
 * This is more permissive than Algolia's `minWordSizefor1Typo: 5`.
 * The ctgSearch engine indexes token prefixes (see buildIndex), so a
 * query like "cev" at length 3 with budget 1 fuzzy-matches the "civ"
 * prefix of "civic" and filters correctly. Without the length-3
 * budget, incremental typing produces no filtering until the user
 * reaches 5 characters, which feels broken for a client-side search.
 */
export function budgetFor(tokenLength) {
  if (tokenLength <= 2) return 0;
  if (tokenLength <= 7) return 1;
  return 2;
}
